import Renderer from './Renderer';
import Bounds from '../utils/Bounds';
import { loaders, createLoader, SpriteImage, EMPTY_SPRITE_IMAGE } from '../loader';

export class UVs {
	topLeft: number;
	topRight: number;
	bottomLeft: number;
	bottomRight: number;

	constructor () {
		this.topLeft = 0;
		this.bottomLeft = 0;
		this.topRight = 0;
		this.bottomRight = 0;
	}

	reset (left: number, right: number, top: number, bottom: number) {
		const cu0 = left   * 0xffff     & 0xffff;
		const cu1 = right  * 0xffff     & 0xffff;
		const cv0 = top    * 0xffff0000 & 0xffff0000;
		const cv1 = bottom * 0xffff0000 & 0xffff0000;

		this.topLeft = cu0 + cv0;
		this.bottomLeft = cu0 + cv1;
		this.topRight = cu1 + cv0;
		this.bottomRight = cu1 + cv1;
	}
}

export class SpriteTexture {
	bounds: Bounds;
	texture: Texture;
	coordinates: UVs;

	constructor (texture: Texture, bounds: Bounds) {
		this.bounds = bounds;
		this.texture = texture;
		this.coordinates = new UVs();
		this.resetCoordinates();
	}

	resetCoordinates () {
		const width = this.texture.width;
		const height = this.texture.height;
		const bounds = this.bounds;
		const left = bounds.left / width;
		const right = bounds.right / width;
		const top = bounds.top / height;
		const bottom = bounds.bottom / height;
		this.coordinates.reset(left, right, top, bottom);
	}
}

// keeping track of set of textures to optimized draw calls:
// - identical combination of textures could be batched rendered

interface textureSetTreeMap {
	[key: string]: TextureSetTree
}

class TextureSetTree {
	node: textureSetTreeMap;
	set: TextureSet;

	constructor (textureSet: TextureSet) {
		this.node = {};
		this.set = textureSet;
	}
}

export class TextureSet {
	set: Array<Texture>;
	coordinates: Array<UVs>;

	constructor (spriteTextures: Array<SpriteTexture>, textureCount: number) {
		this.set = new Array(textureCount);
		this.coordinates = new Array(textureCount);
		for (let s = 0; s < textureCount; s++) {
			const spriteTexture = spriteTextures[s]
			this.set[s] = spriteTexture.texture;
			this.coordinates[s] = spriteTexture.coordinates;
		}
	}
}

function createSpriteTexture (spriteImage: SpriteImage) {
	const image = spriteImage.image;
	const texture = new Texture(image);
	texture.name = spriteImage.id;
	return new SpriteTexture(texture, spriteImage.bounds);
}

const globalTextureSetTree = new TextureSetTree(new TextureSet([], 0));
function getSpriteTextureSet (spriteTextures: Array<SpriteTexture>) {
	let textureSetTree = globalTextureSetTree;
	let textureSetNode = textureSetTree.node;
	for (let t = 0; t < spriteTextures.length; t++) {
		const texture = spriteTextures[t].texture;
		const textureID = texture.id;

		textureSetTree = textureSetNode[textureID];
		if (textureSetTree === undefined) {
			textureSetTree = new TextureSetTree(new TextureSet(spriteTextures, t + 1));
			textureSetNode[textureID] = textureSetTree;
		}

		textureSetNode = textureSetTree.node;
	}

	return textureSetTree.set;
}

async function loadSpriteTexture (url: string) {
	const sprite = await loaders.loadSprite(url);
	return createSpriteTexture(sprite);
}

const spriteTextureLoader = createLoader(loadSpriteTexture, []);
loaders.loadSpriteTexture = spriteTextureLoader.singular;
loaders.loadSpriteTextures = spriteTextureLoader.plural;

export async function loadTexture (url: string, textureOptions?: textureOptions) {
	const spriteTexture = await loaders.loadSpriteTexture(url);
	spriteTexture.texture.setTextureOptions(textureOptions);
	return getSpriteTextureSet([spriteTexture]);
}

export async function loadTextures (urls: Array<string>) {
	const spriteTextures = await loaders.loadSpriteTextures(urls);
	return getSpriteTextureSet(spriteTextures);
}

export type Texturable = HTMLImageElement | HTMLCanvasElement | ImageData | HTMLVideoElement ;
export type textureOptions = {
	alphaPremultiplied?: boolean;
	mipmap?: boolean;
	arrayBuffer?: ArrayBufferView;
	width?: number;
	height?: number;
	minificationFilter?: string;
	magnificationFilter?: string;
	horizontalWrapping?: string;
	verticalWrapping?: string;
};

class TextureUpdate {
	imageData: Texturable;
	destination: Bounds;
	constructor (imageData: Texturable, destination: Bounds) {
		this.imageData = imageData;
		this.destination = destination;
	}
}

let textureUID = 0;

export class Texture {
	private _alphaPremultiplied: boolean | null;
	private _mipmapped: boolean | null;
	private _minificationFilter: string | null;
	private _magnificationFilter: string | null;
	private _horizontalWrapping: string | null;
	private _verticalWrapping: string | null;
	private _width: number;
	private _height: number;

	// TODO: move them out of class properties as they should not be used by every texture?
	private _updateList: Array<TextureUpdate> | null;
	private _dimensionChanged: Array<boolean>;
	private _previousWidth: number;
	private _previousHeight: number;

	id: number;
	name: string;
	imageData: Texturable | null;
	arrayBuffer: ArrayBufferView | null;
	binders: Array<WebGLTexture>;

	constructor (imageData: Texturable | null, textureOptions?: textureOptions) {
		this.id = textureUID++;
		this.name = '';

		this._width = 0;
		this._height = 0;
		this.imageData = null;
		this.arrayBuffer = null;
		this.binders = [];

		this._updateList = null;
		this._dimensionChanged = [];
		this._previousWidth = 0;
		this._previousHeight = 0;

		this._mipmapped = false;
		this._alphaPremultiplied = false;

		this._minificationFilter = null;
		this._magnificationFilter = null;
		this._horizontalWrapping = null;
		this._verticalWrapping = null;

		this.setTextureOptions(textureOptions)
		this.updateTexture(imageData);
	}

	setDimensions (width: number, height: number, preserveContent = false) {
		if (this._width === width && this._height === height) {
			return;
		}

		this._previousWidth = this._width;
		this._previousHeight = this._height;

		this._width = width;
		this._height = height;

		if (preserveContent) {
			for (let i = 0; i < this.binders.length; i++) {
				this._dimensionChanged[i] = true;
			}
		}
	}

	get width () {
		return this._width;
	}

	get height () {
		return this._height;
	}

	setTextureOptions (textureOptions?: textureOptions) {
		// Possible options:
		// wrappings.CLAMP_TO_EDGE =   gl.CLAMP_TO_EDGE;
		// wrappings.REPEAT =          gl.REPEAT;
		// wrappings.MIRRORED_REPEAT = gl.MIRRORED_REPEAT;

		// filterings.NEAREST =                gl.NEAREST;
		// filterings.LINEAR =                 gl.LINEAR;
		// filterings.MIPMAP =                 gl.LINEAR_MIPMAP_LINEAR;
		// filterings.LINEAR_MIPMAP_LINEAR =   gl.LINEAR_MIPMAP_LINEAR;
		// filterings.NEAREST_MIPMAP_NEAREST = gl.NEAREST_MIPMAP_NEAREST;
		// filterings.NEAREST_MIPMAP_LINEAR =  gl.NEAREST_MIPMAP_LINEAR;
		// filterings.LINEAR_MIPMAP_NEAREST =  gl.LINEAR_MIPMAP_NEAREST;

		if (textureOptions !== undefined) {
			this._width = textureOptions.width || 0;
			this._height = textureOptions.height || 0;
			this.arrayBuffer = textureOptions.arrayBuffer || null;
			this._mipmapped = textureOptions.mipmap || null;
			this._alphaPremultiplied = textureOptions.alphaPremultiplied || false;

			this._minificationFilter  = textureOptions.minificationFilter || null;
			this._magnificationFilter = textureOptions.magnificationFilter || null;
			this._horizontalWrapping  = textureOptions.horizontalWrapping || null;
			this._verticalWrapping    = textureOptions.verticalWrapping || null;
		}
	}

	updateTexture (imageData: Texturable | null) {
		this.imageData = imageData;
		if (imageData !== null) {
			this._width = imageData.width;
			this._height = imageData.height;
		}
	}

	_createTexture (gl: WebGLRenderingContext) {
		const textureBinder = gl.createTexture();
		gl.bindTexture(gl.TEXTURE_2D, textureBinder);

		const minFilter = this._minificationFilter === null ? gl.LINEAR : gl[this._minificationFilter];
		const maxFilter = this._magnificationFilter === null ? gl.LINEAR : gl[this._magnificationFilter];
		const horizontalWrapping = this._horizontalWrapping === null ? gl.CLAMP_TO_EDGE : gl[this._horizontalWrapping];
		const verticalWrapping = this._verticalWrapping === null ? gl.CLAMP_TO_EDGE : gl[this._verticalWrapping];

		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, minFilter);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, maxFilter);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, horizontalWrapping);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, verticalWrapping);

		return textureBinder;
	}

	// TODO: make updatable texture a subclass of Texture?
	addToUpdateList (imageData: Texturable, destination: Bounds) {
		if (this._updateList === null) {
			this._updateList = [];
		}

		this._updateList.push(new TextureUpdate(imageData, destination));
	}

	bindTexture (rendererID: number, gl: WebGLRenderingContext) {
		const binder = this.binders[rendererID];
		const dimensionsChanged = this._dimensionChanged[rendererID];
		if (dimensionsChanged || binder === undefined) {
			if (dimensionsChanged && binder !== undefined) {
				gl.bindTexture(gl.TEXTURE_2D, binder);
				
				const frameBufferBinder = gl.createFramebuffer();
				const activeFrameBuffer = gl.getParameter(gl.FRAMEBUFFER_BINDING);
				gl.bindFramebuffer(gl.FRAMEBUFFER, frameBufferBinder);
				gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, binder, 0);

				// Copying current texture content for reupload
				const width = this._previousWidth;
				const height = this._previousHeight;
				const pixelBuffer = new Uint8Array(width * height * 4);
				gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixelBuffer);
				
				gl.bindFramebuffer(gl.FRAMEBUFFER, activeFrameBuffer);

				let nonZeroCount = 0;
				for (let i = 0; i < pixelBuffer.length;i ++) {
					if (pixelBuffer[i] > 0) {
						nonZeroCount += 1;
					}
				}

				// Resetting texture dimensions
				gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this._width, this._height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

				// Uploading saved texture content
				gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixelBuffer);
			} else {
				let newBinder = this._createTexture(gl);
				if (newBinder === null) {
					throw new Error('Could not create texture');
				}
	
				this.binders[rendererID] = newBinder;
				this._uploadTexture(gl);
			}
			this._dimensionChanged[rendererID] = false;
		} else {
			gl.bindTexture(gl.TEXTURE_2D, binder);
		}

		if (this._updateList !== null) {
			this._uploadSubTextures(gl);
		}
	}

	private _uploadSubTextures (gl: WebGLRenderingContext) {
		if (this._updateList === null) {
			return;
		}

		// console.error('uploadSubTextures!', this._updateList);
		for (let t = 0; t < this._updateList.length; t++) {
			const textureUpdate = this._updateList[t];
			const destination = textureUpdate.destination;
			gl.texSubImage2D(gl.TEXTURE_2D, 0, destination.left, destination.top, gl.RGBA, gl.UNSIGNED_BYTE, textureUpdate.imageData);
		}

		this._updateList = null;
	}

	refreshTexture (renderer: Renderer) {
		const gl = renderer.gl;
		this.bindTexture(renderer.id, gl);
		this._uploadTexture(gl);
	}

	_uploadTexture (gl: WebGLRenderingContext) {
		if (this.imageData !== null) {
			// See https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/texImage2D for possible types
			// TODO: verify type?
			// imageData instanceof Image ||
			// imageData instanceof ImageData ||
			// imageData instanceof HTMLImageElement ||
			// imageData instanceof HTMLCanvasElement ||
			// imageData instanceof HTMLVideoElement

			// Alpha premultiplication may be applied when loading the texture
			gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, !this._alphaPremultiplied);

			// Loading the image onto the GPU
			gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.imageData);
		} else {
			// Loading the array buffer onto the GPU
			gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this._width, this._height, 0, gl.RGBA, gl.UNSIGNED_BYTE, this.arrayBuffer);
		}

		if (this._mipmapped) {
			this._generateMipmap(gl);
		}
	}

	_generateMipmap (gl: WebGLRenderingContext) {
		function isPowerOfTwo(n: number) {
			return (n & (n - 1)) === 0;
		}

		if (!isPowerOfTwo(this._width) || !isPowerOfTwo(this._height)) {
			// Mipmap requested
			console.error('Can only build mip maps for texture of power of 2 dimensions, dimesions: ' + this._width + 'x' + this._height);
			return;
		}

		gl.generateMipmap(gl.TEXTURE_2D);
	}

	// delete () {
	// 	const renderers = renderers;
	// 	for (let r = 0; r < renderers.length; r++) {
	// 		const renderer = renderers[r];
	// 		renderer.gl.deleteTexture(this.binders[renderer.id]);
	// 	}
	// 	this.binders = [];
	// }
}

export default Texture;

export const EMPTY_TEXTURE = new Texture(EMPTY_SPRITE_IMAGE.image);

// export const EMPTY_TEXTURE_SET = new TextureSet([createSpriteTexture(EMPTY_SPRITE_IMAGE)], 1);
export const EMPTY_TEXTURE_SET = new TextureSet([], 0);
